import { supabase } from '../lib/supabase'
import { supportCenterAssistancesService } from './supportCenterAssistances'
import type {
  SupportCenterProfileOption,
  SupportCenterSpecialization,
  SupportCenterSpecializationAssistanceRef,
  SupportCenterSpecializationAssignment,
  SupportCenterSpecializationInput,
  SupportCenterUserAvailability,
  SupportCenterUserAvailabilityInput,
  SupportCenterUserUnavailability,
  SupportCenterUserUnavailabilityInput,
} from '../types/supportCenterSpecialties'

function sanitizeIntList(values: number[], min: number, max: number): number[] {
  const seen = new Set<number>()
  const sanitized: number[] = []

  for (const raw of values) {
    const n = Number(raw)
    if (!Number.isInteger(n) || n < min || n > max) continue
    if (seen.has(n)) continue
    seen.add(n)
    sanitized.push(n)
  }

  return sanitized.sort((a, b) => a - b)
}

function asIntArray(value: unknown): number[] {
  if (!Array.isArray(value)) return []
  return value.map(Number).filter((n) => Number.isInteger(n))
}

function catalogId(row: Record<string, unknown>): string {
  return String(row.specialty_id ?? row.professional_id ?? row.specialization_id ?? '')
}

async function loadAssistancesBySpecializationIds(
  specializationIds: string[]
): Promise<Map<string, SupportCenterSpecializationAssistanceRef[]>> {
  const map = new Map<string, SupportCenterSpecializationAssistanceRef[]>()
  const ids = [...new Set(specializationIds.filter(Boolean))]
  if (ids.length === 0) return map

  const { data, error } = await supabase
    .from('support_center_specialty_assistances')
    .select(`
      specialty_id,
      assistance:support_center_assistances!assistance_id(id, name, code, deleted_at)
    `)
    .in('specialty_id', ids)
    .is('deleted_at', null)

  if (error) throw error

  for (const row of data ?? []) {
    const specializationId = catalogId(row as Record<string, unknown>)
    if (!specializationId) continue
    const raw = row.assistance
    const assistance = Array.isArray(raw) ? raw[0] : raw
    if (!assistance || typeof assistance !== 'object') continue
    const a = assistance as {
      id?: string
      name?: string | null
      code?: string | null
      deleted_at?: string | null
    }
    if (!a.id || a.deleted_at) continue
    const list = map.get(specializationId) ?? []
    list.push({
      id: a.id,
      name: (a.name ?? '').trim(),
      code: (a.code ?? '').trim(),
    })
    map.set(specializationId, list)
  }

  for (const [key, list] of map) {
    list.sort((left, right) => {
      const byCode = left.code.localeCompare(right.code, 'pt-BR', { sensitivity: 'base' })
      if (byCode !== 0) return byCode
      return left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' })
    })
    map.set(key, list)
  }

  return map
}

export const supportCenterSpecialtiesService = {
  async listSpecializations(): Promise<SupportCenterSpecialization[]> {
    const { data, error } = await supabase
      .from('support_center_specialties')
      .select('*')
      .is('deleted_at', null)
      .order('name')

    if (error) throw error

    const rows = data ?? []
    const assistancesBySpec = await loadAssistancesBySpecializationIds(
      rows.map((row) => String(row.id))
    )

    return rows.map((row) => {
      const id = String(row.id)
      return {
        ...(row as Omit<SupportCenterSpecialization, 'assistances'>),
        id,
        assistances: assistancesBySpec.get(id) ?? [],
      }
    })
  },

  async upsertSpecialization(
    input: SupportCenterSpecializationInput,
    actorId?: string | null
  ): Promise<string> {
    const assistanceIds = [...new Set(input.assistance_ids.filter(Boolean))]
    if (assistanceIds.length === 0) {
      throw new Error('Vincule ao menos uma assistência.')
    }

    const payload = {
      name: input.name.trim(),
      description: input.description.trim() || null,
    }

    let specializationId = input.id ?? ''

    if (input.id) {
      const { error } = await supabase
        .from('support_center_specialties')
        .update(payload)
        .eq('id', input.id)
      if (error) throw error
    } else {
      const { data, error } = await supabase
        .from('support_center_specialties')
        .insert(payload)
        .select('id')
        .single()
      if (error) throw error
      specializationId = String(data.id)
    }

    await supportCenterAssistancesService.syncSpecializationAssistances(
      specializationId,
      assistanceIds,
      actorId ?? null
    )
    return specializationId
  },

  async softDeleteSpecialization(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('support_center_specialties')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  async listAssignments(): Promise<SupportCenterSpecializationAssignment[]> {
    const { data, error } = await supabase
      .from('support_center_specialty_users')
      .select(`
        *,
        profile:profiles!user_id(id, name),
        specialization:support_center_specialties!specialty_id(id, name)
      `)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data ?? []).map((row) => {
      const raw = row as Record<string, unknown>
      return {
        ...raw,
        specialization_id: catalogId(raw),
      } as SupportCenterSpecializationAssignment
    })
  },

  async assignSpecialization(
    userId: string,
    specializationId: string,
    createdBy: string
  ): Promise<void> {
    await this.assignSpecializations(userId, [specializationId], createdBy)
  },

  async assignSpecializations(
    userId: string,
    specializationIds: string[],
    createdBy: string
  ): Promise<void> {
    const uniqueIds = [...new Set(specializationIds.filter(Boolean))]
    if (uniqueIds.length === 0) {
      throw new Error('Selecione ao menos uma especialidade.')
    }

    const { error } = await supabase.from('support_center_specialty_users').insert(
      uniqueIds.map((specializationId) => ({
        user_id: userId,
        specialty_id: specializationId,
        created_by: createdBy,
      }))
    )

    if (error) throw error
  },

  async syncUserSpecializations(
    userId: string,
    specializationIds: string[],
    actorId: string
  ): Promise<void> {
    const desired = [...new Set(specializationIds.filter(Boolean))]
    if (desired.length === 0) {
      throw new Error('Selecione ao menos uma especialidade.')
    }

    const { data, error } = await supabase
      .from('support_center_specialty_users')
      .select('id, specialty_id')
      .eq('user_id', userId)
      .is('deleted_at', null)

    if (error) throw error

    const current = (data ?? []) as Array<{
      id: string
      specialty_id?: string
      professional_id?: string
      specialization_id?: string
    }>
    const currentBySpec = new Map(
      current.map((row) => [catalogId(row as unknown as Record<string, unknown>), row.id])
    )
    const desiredSet = new Set(desired)

    const toAdd = desired.filter((id) => !currentBySpec.has(id))
    const toRemove = current.filter(
      (row) => !desiredSet.has(catalogId(row as unknown as Record<string, unknown>))
    )

    if (toAdd.length > 0) {
      await this.assignSpecializations(userId, toAdd, actorId)
    }

    for (const row of toRemove) {
      await this.softDeleteAssignment(
        row.id,
        'Removido na edição das especialidades do profissional',
        actorId
      )
    }
  },

  async updateAssignment(id: string, specializationId: string): Promise<void> {
    const { error } = await supabase
      .from('support_center_specialty_users')
      .update({ specialty_id: specializationId })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  async softDeleteAssignment(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('support_center_specialty_users')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  async softDeleteAllAssignmentsForUser(
    targetUserId: string,
    reason: string,
    actorId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('support_center_specialty_users')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: actorId,
        deleted_reason: reason,
      })
      .eq('user_id', targetUserId)
      .is('deleted_at', null)

    if (error) throw error
  },

  async listActiveProfiles(): Promise<SupportCenterProfileOption[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, name')
      .eq('is_active', true)
      .order('name')

    if (error) throw error
    return (data ?? []) as SupportCenterProfileOption[]
  },

  /** Profissionais (pessoas) com ao menos um vínculo ativo em support_center_specialty_users */
  async listAssignedProfiles(): Promise<SupportCenterProfileOption[]> {
    const { data, error } = await supabase
      .from('support_center_specialty_users')
      .select(`
        user_id,
        profile:profiles!user_id(id, name, is_active)
      `)
      .is('deleted_at', null)

    if (error) throw error

    const byId = new Map<string, SupportCenterProfileOption>()
    for (const row of data ?? []) {
      const profileRaw = row.profile
      const profile = Array.isArray(profileRaw) ? profileRaw[0] : profileRaw
      if (!profile || typeof profile !== 'object') continue
      const p = profile as { id?: string; name?: string | null; is_active?: boolean }
      if (!p.id || p.is_active === false) continue
      if (!byId.has(p.id)) {
        byId.set(p.id, { id: p.id, name: p.name ?? '' })
      }
    }

    return [...byId.values()].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR', { sensitivity: 'base' })
    )
  },

  async listAvailability(): Promise<SupportCenterUserAvailability[]> {
    const { data, error } = await supabase
      .from('support_center_user_availability')
      .select('*')
      .is('deleted_at', null)
      .order('updated_at', { ascending: false })

    if (error) throw error
    return (data ?? []).map((row) => ({
      ...(row as SupportCenterUserAvailability),
      weekdays: asIntArray(row.weekdays),
      hours: asIntArray(row.hours),
    }))
  },

  async upsertAvailability(input: SupportCenterUserAvailabilityInput): Promise<void> {
    const weekdays = sanitizeIntList(input.weekdays, 0, 6)
    const hours = sanitizeIntList(input.hours, 0, 23)

    const { data: existing, error: findError } = await supabase
      .from('support_center_user_availability')
      .select('id')
      .eq('user_id', input.user_id)
      .is('deleted_at', null)
      .maybeSingle()

    if (findError) throw findError

    if (existing?.id) {
      const { error } = await supabase
        .from('support_center_user_availability')
        .update({
          weekdays,
          hours,
          updated_by: input.actor_id,
        })
        .eq('id', existing.id)
      if (error) throw error
      return
    }

    const { error } = await supabase.from('support_center_user_availability').insert({
      user_id: input.user_id,
      weekdays,
      hours,
      created_by: input.actor_id,
      updated_by: input.actor_id,
    })
    if (error) throw error
  },

  async softDeleteAvailability(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('support_center_user_availability')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  async listUnavailability(): Promise<SupportCenterUserUnavailability[]> {
    const { data, error } = await supabase
      .from('support_center_user_unavailability')
      .select(`
        *,
        profile:profiles!user_id(id, name)
      `)
      .is('deleted_at', null)
      .order('starts_on', { ascending: false })

    if (error) throw error
    return (data ?? []).map((row) => ({
      ...(row as SupportCenterUserUnavailability),
      hours: asIntArray(row.hours),
    }))
  },

  async upsertUnavailability(input: SupportCenterUserUnavailabilityInput): Promise<string> {
    const allDay = input.all_day
    const hours = allDay ? [] : sanitizeIntList(input.hours, 0, 23)

    if (!allDay && hours.length === 0) {
      throw new Error('Selecione ao menos uma hora quando não for dia inteiro.')
    }
    if (input.ends_on < input.starts_on) {
      throw new Error('A data final deve ser maior ou igual à data inicial.')
    }

    const payload = {
      user_id: input.user_id,
      starts_on: input.starts_on,
      ends_on: input.ends_on,
      all_day: allDay,
      hours,
      reason: input.reason.trim() || null,
    }

    if (input.id) {
      const { error } = await supabase
        .from('support_center_user_unavailability')
        .update(payload)
        .eq('id', input.id)
      if (error) throw error
      return input.id
    }

    const { data, error } = await supabase
      .from('support_center_user_unavailability')
      .insert({
        ...payload,
        created_by: input.actor_id,
      })
      .select('id')
      .single()
    if (error) throw error
    return String(data.id)
  },

  async softDeleteUnavailability(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('support_center_user_unavailability')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },
}
